import { useState, useEffect } from "react";
import { ArrowRight, Clock, MapPin } from "lucide-react";
import { getAllGlamPackages } from "../../services/api";
import { useNavigate } from "react-router-dom";

interface SessionType {
  id: string;
  title: string;
  price: number;
  description: string;
  image: string;
}

interface SessionCarouselProps {
  onSessionSelect: (sessionId: string) => void;
}

export function SessionCarousel({ onSessionSelect }: SessionCarouselProps) {
  const [sessions, setSessions] = useState<SessionType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadPackages = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = (await getAllGlamPackages()) as any;
      const packages = response.data.data.map((pkg: any) => ({
        id: pkg.id,
        title: pkg.title,
        price: pkg.price,
        description: pkg.description,
        image: pkg.media_url || "/images/default-image.svg",
      }));
      setSessions(packages);
    } catch (err) {
      console.error("Failed to fetch glam packages:", err);
      setError("We couldn't load our packages just now.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPackages();
  }, []);

  const handleGetQuote = (sessionId: string) => navigate(`/quote-form/${sessionId}`);

  return (
    <div className="min-h-screen bg-[#FBEFEF]">
      <div className="mx-auto w-full max-w-5xl px-4 py-10 md:py-16">
        {/* Editorial header */}
        <header className="mx-auto max-w-2xl text-center">
          <p className="text-xs uppercase tracking-[0.32em] text-[#B87775]">
            Book a session
          </p>
          <h1 className="mt-3 font-serif text-3xl leading-tight text-[#3a2e2c] md:text-5xl">
            Choose your glam
          </h1>
          <p className="mt-4 text-base leading-relaxed text-[#7c6c6a] md:text-lg">
            Every package is tailored by hand in our Halifax studio. Pick the one
            that fits your moment — you can refine the details next.
          </p>
        </header>

        {/* Loading */}
        {isLoading && (
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <div
                key={i}
                className="overflow-hidden rounded-2xl border border-[#EAD7D6] bg-white"
              >
                <div className="h-48 animate-pulse bg-[#F1DEDD]" />
                <div className="space-y-3 p-6">
                  <div className="h-5 w-2/3 animate-pulse rounded bg-[#F1DEDD]" />
                  <div className="h-4 w-full animate-pulse rounded bg-[#F1DEDD]" />
                  <div className="h-4 w-4/5 animate-pulse rounded bg-[#F1DEDD]" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {!isLoading && error && (
          <div
            role="alert"
            className="mx-auto mt-12 max-w-md rounded-2xl border border-[#EAD7D6] bg-white px-6 py-8 text-center"
          >
            <p className="text-[#7c6c6a]">{error}</p>
            <button
              type="button"
              onClick={loadPackages}
              className="mt-4 rounded-full bg-[#B87775] px-6 py-2.5 text-sm font-medium text-white transition hover:bg-[#a96764] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2"
            >
              Try again
            </button>
          </div>
        )}

        {/* Empty */}
        {!isLoading && !error && sessions.length === 0 && (
          <div className="mx-auto mt-12 max-w-md rounded-2xl border border-dashed border-[#E3C9C8] bg-white/60 px-6 py-10 text-center text-[#9A6B69]">
            No packages are available right now. Please check back soon.
          </div>
        )}

        {/* Package menu */}
        {!isLoading && !error && sessions.length > 0 && (
          <ul
            className={`mt-12 grid grid-cols-1 gap-6 ${
              sessions.length === 1
                ? "mx-auto max-w-md"
                : "md:grid-cols-2"
            }`}
          >
            {sessions.map((session, index) => (
              <li
                key={session.id}
                style={{ animationDelay: `${index * 80}ms` }}
                className="animate-charme-rise group flex flex-col overflow-hidden rounded-2xl border border-[#EAD7D6] bg-white shadow-[0_1px_2px_rgba(184,119,117,0.06)] transition-shadow duration-300 hover:shadow-[0_12px_32px_rgba(184,119,117,0.16)]"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={session.image}
                    alt={session.title}
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                  />
                  <div className="absolute right-4 top-4 rounded-full bg-white/90 px-3 py-1 font-serif text-lg tabular-nums text-[#B87775] shadow-sm backdrop-blur">
                    ${session.price}
                  </div>
                </div>

                <div className="flex flex-1 flex-col p-6">
                  <h2 className="font-serif text-2xl leading-tight text-[#3a2e2c]">
                    {session.title}
                  </h2>

                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#9A6B69]">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                      Studio or home
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                      Times confirmed at booking
                    </span>
                  </div>

                  <p className="mt-4 flex-1 text-[15px] leading-relaxed text-[#7c6c6a]">
                    {session.description}
                  </p>

                  <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                    <button
                      type="button"
                      onClick={() => onSessionSelect(session.id)}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-[#B87775] px-5 py-3 text-sm font-medium text-white transition hover:bg-[#a96764] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2"
                    >
                      Book this
                      <ArrowRight
                        className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5"
                        aria-hidden="true"
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleGetQuote(session.id)}
                      className="inline-flex flex-1 items-center justify-center rounded-full border border-[#E3C9C8] px-5 py-3 text-sm font-medium text-[#B87775] transition hover:border-[#B87775] hover:bg-[#FBEFEF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2"
                    >
                      Get a quote
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
